import Spinner from '@rio-cloud/rio-uikit/Spinner';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { IPagesForUserManualDialog } from '@/features/userManual/utils';
import { t } from '@/lib/utils';

const WelcomePageDialogContent = React.memo(
    ({ keyPage, imageSrc, listSubtitles, listKeys }: IPagesForUserManualDialog) => {
        const [showSpinner, setShowSpinner] = useState(true);
        const [imageLoaded, setImageLoaded] = useState(false);

        const sanitizeHtml = useCallback((key: string) => {
            const translatedHTML = t(key);
            return DOMPurify.sanitize(translatedHTML);
        }, []);

        useEffect(() => {
            setShowSpinner(true);
            setImageLoaded(false);
        }, [imageSrc]);

        const handleImageLoad = () => {
            setImageLoaded(true);
            setShowSpinner(false);
        };

        return (
            <>
                <h4
                    data-testid='user-manual-service-modal-title'
                    className='text-normal text-transform-none margin-top-20 margin-bottom-15'
                >
                    <FormattedMessage id={`userManual.${keyPage}.title`} />
                </h4>
                <div className='position-relative width-100pct'>
                    {showSpinner && (
                        <div className='display-flex align-items-center justify-content-center min-height-400'>
                            <Spinner />
                        </div>
                    )}
                    <img
                        src={imageSrc}
                        alt={t(`userManual.${keyPage}.imageAlt`)}
                        data-test-id='user-manual-modal-image'
                        height={410}
                        className={`width-100pct height-100pct ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        style={{ objectFit: 'contain' }}
                        onLoad={handleImageLoad}
                    />
                </div>

                {listSubtitles?.map(subtitleKey => (
                    <div key={subtitleKey} className='margin-top-25'>
                        <h4 style={{ fontWeight: 700 }}>
                            <FormattedMessage id={`userManual.${keyPage}.${subtitleKey}`} />
                        </h4>
                        {listKeys
                            .filter(desc => desc.includes(subtitleKey))
                            .map((key, index) => (
                                <p
                                    key={index}
                                    // biome-ignore lint: sanitized content with DOMPurify
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(`userManual.${keyPage}.${key}`),
                                    }}
                                />
                            ))}
                    </div>
                ))}
            </>
        );
    }
);

WelcomePageDialogContent.displayName = 'UserManualDialogContent';

export default WelcomePageDialogContent;
